"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteMetaCredential,
  getMetaCredentials,
  initiateMetaNodeAuthorization,
} from "@/actions/workflows/meta";
import { useNamespacedAlert } from "@/hooks/useNamespacedAlert";
import { META_OAUTH_POPUP_NAME } from "@/types/meta";
import type { NodeProviderCredential } from "@/types/workflows";
import {
  handledSessionExpiry,
  settled,
  unwrapSessionExpiry,
} from "@/utils/common/sessionExpiry";
import {
  navigateToAuthorizationUrl,
  openOAuthPopup,
  waitForPopupClose,
} from "@/utils/integrations/oauthPopup";

/**
 * `loadError` is separate from an empty list on purpose: a transient failure must
 * not render as "no Page connected", because the tile would then show the
 * disconnected pitch and hide the Pages that are in fact live.
 */
export type MetaCredentialsStatus = "loading" | "loaded" | "loadError";

/**
 * `superseded` is not a failure: a newer read has already published its rows, so
 * this one has nothing left to say. Collapsing it into either of the other two
 * is what lets an older resolve restore rows the customer just changed.
 */
type ReadOutcome =
  | { status: "current"; rows: NodeProviderCredential[] }
  | { status: "superseded" }
  | { status: "failed" };

interface ReadOptions {
  /**
   * A credential id to drop from whatever comes back. Omega's list is read from
   * a replica, so a refetch fired immediately after a successful delete can
   * still contain the deleted row — and resurrecting it under the success toast
   * leaves the customer one click away from a 404.
   */
  excludeId?: string;
}

export function useMetaCredentials() {
  const raise = useNamespacedAlert("metaIntegration");

  const [status, setStatus] = useState<MetaCredentialsStatus>("loading");
  const [credentials, setCredentials] = useState<NodeProviderCredential[]>([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);

  // The rows as last known rather than as last rendered, so `connect` cannot
  // diff against a stale closure.
  const credentialsRef = useRef<NodeProviderCredential[]>([]);

  /**
   * Bumped by every read; only the read that still owns the latest number is
   * allowed to publish. Reads are fired from three places that can overlap
   * (`load`, `connect`'s refetch, `disconnect`'s refetch), and the responses are
   * not ordered.
   */
  const readGenerationRef = useRef(0);

  // The watch on the current popup, aborted on unmount so its poll cannot outlive
  // the tile.
  const popupWatchRef = useRef<AbortController | null>(null);
  useEffect(() => () => popupWatchRef.current?.abort(), []);

  /**
   * `failed` is "couldn't tell", not "error state" — each caller decides. The
   * rows come back so `connect` can diff without waiting for a re-render.
   */
  const read = useCallback(
    async ({ excludeId }: ReadOptions = {}): Promise<ReadOutcome> => {
      const generation = ++readGenerationRef.current;
      const rows = await settled(getMetaCredentials);
      if (!rows) return { status: "failed" };

      if (generation !== readGenerationRef.current) return { status: "superseded" };

      // Omega lists revoked rows too, and a revoked Page is not connected: it
      // must not count toward the badge, the panel, or `connect`'s diff.
      const visible = rows.filter(
        (credential) =>
          credential.status.toLowerCase() === "active" &&
          credential.id !== excludeId
      );

      credentialsRef.current = visible;
      setCredentials(visible);
      setStatus("loaded");
      return { status: "current", rows: visible };
    },
    []
  );

  const load = useCallback(async () => {
    setStatus("loading");
    const outcome = await read();
    if (outcome.status === "failed") {
      setStatus("loadError");
      raise("error", "loadError");
    }
  }, [raise, read]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll-then-refetch rather than `postMessage` — see
  // `@/utils/integrations/oauthPopup` and `docs/meta-integration.md`.
  const connect = useCallback(async () => {
    // Opened before the await so Safari does not treat it as unsolicited.
    const popup = openOAuthPopup(META_OAUTH_POPUP_NAME);

    setIsConnecting(true);
    try {
      const authorizationResult = await initiateMetaNodeAuthorization();
      if (handledSessionExpiry(authorizationResult)) {
        popup?.close();
        return;
      }
      const authorizationUrl = unwrapSessionExpiry(authorizationResult, null);
      if (!authorizationUrl) throw new Error("Meta authorization URL not returned");

      // Throws on anything but https, into the catch below — the popup is
      // same-origin with us until it navigates, so an unvetted URL would run in
      // our origin.
      if (navigateToAuthorizationUrl(popup, authorizationUrl) === "redirect") return;
      if (!popup) return;

      popupWatchRef.current?.abort();
      const watch = new AbortController();
      popupWatchRef.current = watch;
      const outcome = await waitForPopupClose(popup, watch.signal);

      // Aborted means the tile unmounted or a newer connect superseded this one,
      // so this attempt has no one left to report to.
      if (watch.signal.aborted) return;

      // Snapshotted after the popup closes, so only what the grant itself added
      // is named.
      const before = new Set(
        credentialsRef.current.map((credential) => credential.id)
      );

      // A timeout means the popup may still be in use, so the refetch runs but
      // says nothing out loud about a flow that has not finished.
      const refetch = await read();
      if (refetch.status === "failed") {
        if (outcome === "closed") {
          setStatus("loadError");
          raise("error", "loadError");
        }
        return;
      }

      if (refetch.status === "superseded") return;

      const added = refetch.rows.filter((credential) => !before.has(credential.id));
      if (added.length === 0) {
        // Ambiguous, not a failure: reconnecting a Page keeps its credential id,
        // so a grant that worked is indistinguishable from one that was refused.
        if (outcome === "closed") raise("info", "noNewPage");
        return;
      }

      raise("success", "connectedPages", {
        names: added.map((credential) => credential.label).join(", "),
        count: added.length,
      });
    } catch (error) {
      popup?.close();
      console.error("useMetaCredentials: failed to start the Meta connection", error);
      raise("error", "connectError");
    } finally {
      setIsConnecting(false);
    }
  }, [raise, read]);

  const disconnect = useCallback(
    async (credentialId: string, name: string): Promise<boolean> => {
      setDisconnectingId(credentialId);
      try {
        // The modal says more than a toast could, so the row stays put.
        if (handledSessionExpiry(await deleteMetaCredential(credentialId))) {
          return false;
        }
      } catch (error) {
        console.error("useMetaCredentials: failed to disconnect the Page", error);
        raise("error", "disconnectError");
        return false;
      } finally {
        setDisconnectingId(null);
      }

      // Dropped locally first: the delete succeeded, so a failed refetch below
      // must not undo it.
      const remaining = credentialsRef.current.filter(
        (credential) => credential.id !== credentialId
      );
      credentialsRef.current = remaining;
      setCredentials(remaining);
      raise("success", "disconnected", { name });

      await read({ excludeId: credentialId });
      return true;
    },
    [raise, read]
  );

  return {
    status,
    credentials,
    isConnecting,
    disconnectingId,
    load,
    connect,
    disconnect,
  };
}

export type MetaCredentialsController = ReturnType<typeof useMetaCredentials>;
